import { useState, useEffect, useMemo, useCallback } from "react";
import round from 'lodash/round';
import { 
  getAreas, getCompanyUsers, getCompanyData, 
  getAreaUsers, getAreaData, getUserIndividualData, 
  getUserIndividualSleep, getUserIndividualHeartRate,
  getPoblationalData, getDiseases, postManualHealthData
} from '../../../helpers/satiBack';
import { 
  getMockData, getMockUsers, getMockAreas, 
  getMockUserIndividualData, getMockUserIndividualSleep, 
  getMockUserIndividualHeartRate, getMockAreaData,
  getMockPoblationalData, getMockDiseases
} from '../../../helpers/mockData';
import { stressRanges, cardiovascularHealthRanges, sleepScoreRanges } from '../../../helpers/statCardRanges';
import { getTableValBadge, makeClickable } from '../../../helpers/dashHelper';
import moment from "moment";
import useReportFilter from "../../../hooks/useReportFilter";
import { calculateDashboardAggregates, COMPANY_DASHBOARD_METRICS } from "../../../utils/calculateDashboardAggregates";

/**
 * Custom hook to centralize Company Dashboard data fetching and state management.
 */
export const useCompanyDashboardData = () => {
  const [areaData, setAreaData] = useState([]);
  const [selectedArea, setSelectedArea] = useState(undefined);
  const [selectedUser, setSelectedUser] = useState(undefined);
  const [diseases, setDiseases] = useState([]);
  const [selectedDisease, setSelectedDisease] = useState("");
  const [userData, setUserData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [prevChartData, setPrevChartData] = useState([]);
  const [heartRateData, setHeartRateData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [cardsData, setCardsData] = useState([]);
  const [loadingBtn, setLoadingBtn] = useState(0);
  const [error, setError] = useState(null);
  const [searchByUser, setSearchByUser] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [poblationalData, setPoblationalData] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const { 
    range: dateRange, 
    setRange: setDateRange, 
    activeFilter, 
    setWeekly, 
    setMonthly, 
    setCustom 
  } = useReportFilter({
    initialRange: [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
    useIsoWeek: true,
    onFilterChange: () => {} // Logic moved to centralized useEffect
  });

  /**
   * Safe fetch helper to handle satiBack helpers that return Error objects
   */
  const fetchSafe = async (promise) => {
    const data = await promise;
    if (data instanceof Error) throw data;
    return data;
  };

  const handleSelectedDisease = useCallback((diseaseName) => {
    setSelectedDisease(diseaseName);
    setSelectedUser(undefined);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchDiseaseCatalog = async () => {
      try {
        const catalog = await fetchSafe(
          (useMockData ? getMockDiseases : getDiseases)()
        );
        if (isMounted) setDiseases(Array.isArray(catalog) ? catalog : []);
      } catch (err) {
        console.error("Disease Catalog Fetch Error:", err);
        if (isMounted) setDiseases([]);
      }
    };

    fetchDiseaseCatalog();
    return () => {
      isMounted = false;
    };
  }, [useMockData]);

  // Centralized data fetching coordination
  useEffect(() => {
    const fetchData = async () => {
      const [start, end] = dateRange;
      if (!start || !end) return;

      const prevStart = start.clone().subtract(1, "week");
      const prevEnd = end.clone().subtract(1, "week");

      setLoadingBtn(activeFilter || 1);
      setError(null);

      const invokeWithDisease = (request, args) => request(
        ...args,
        ...(selectedDisease ? [selectedDisease] : [])
      );

      try {
        const pobData = await fetchSafe(
          invokeWithDisease(
            useMockData ? getMockPoblationalData : getPoblationalData,
            [start, end]
          )
        );
        setPoblationalData(pobData && pobData[0] ? pobData[0] : null);

        if (selectedUser) {
          // Priority 1: User View
          setSearchByUser(true);
          const userPromise = selectedArea
            ? fetchSafe(invokeWithDisease(
                useMockData ? getMockUsers : getAreaUsers,
                [start, end, selectedArea.id || selectedArea.AreaId]
              ))
            : fetchSafe(invokeWithDisease(
                useMockData ? getMockUsers : getCompanyUsers,
                [start, end]
              ));
          const [indData, prevIndData, sleep, hr, users] = await Promise.all([
            fetchSafe(invokeWithDisease(
              useMockData ? getMockUserIndividualData : getUserIndividualData,
              [start, end, selectedUser]
            )),
            fetchSafe(invokeWithDisease(
              useMockData ? getMockUserIndividualData : getUserIndividualData,
              [prevStart, prevEnd, selectedUser]
            )),
            fetchSafe((useMockData ? getMockUserIndividualSleep : getUserIndividualSleep)(start, end, selectedUser)),
            fetchSafe((useMockData ? getMockUserIndividualHeartRate : getUserIndividualHeartRate)(start, end, selectedUser)),
            userPromise
          ]);
          setChartData(indData);
          setPrevChartData(prevIndData);
          setCardsData(calculateDashboardAggregates(indData, COMPANY_DASHBOARD_METRICS));
          setSleepData(sleep);
          setHeartRateData(hr);
          setUserData(users);
        } else if (selectedArea) {
          // Priority 2: Area View
          setSearchByUser(false);
          setSleepData([]); // Cleanup user-specific data
          setHeartRateData([]); // Cleanup user-specific data
          const id = selectedArea.id || selectedArea.AreaId;
          const [users, areaResData, prevAreaResData] = await Promise.all([
            fetchSafe(invokeWithDisease(
              useMockData ? getMockUsers : getAreaUsers,
              [start, end, id]
            )),
            fetchSafe(invokeWithDisease(
              useMockData ? getMockAreaData : getAreaData,
              [start, end, id]
            )),
            fetchSafe(invokeWithDisease(
              useMockData ? getMockAreaData : getAreaData,
              [prevStart, prevEnd, id]
            ))
          ]);
          setUserData(users);
          setChartData(areaResData);
          setPrevChartData(prevAreaResData);
          setCardsData(calculateDashboardAggregates(areaResData, COMPANY_DASHBOARD_METRICS));
        } else {
          // Priority 3: Company View
          setSearchByUser(false);
          setSleepData([]); // Cleanup user-specific data
          setHeartRateData([]); // Cleanup user-specific data
          const [areas, users, companyResData, prevCompanyResData] = await Promise.all([
            fetchSafe(invokeWithDisease(
              useMockData ? getMockAreas : getAreas,
              [start, end]
            )),
            fetchSafe(invokeWithDisease(
              useMockData ? getMockUsers : getCompanyUsers,
              [start, end]
            )),
            fetchSafe(invokeWithDisease(
              useMockData ? getMockData : getCompanyData,
              [start, end]
            )),
            fetchSafe(invokeWithDisease(
              useMockData ? getMockData : getCompanyData,
              [prevStart, prevEnd]
            ))
          ]);
          setAreaData(areas);
          setUserData(users);
          setChartData(companyResData);
          setPrevChartData(prevCompanyResData);
          setCardsData(calculateDashboardAggregates(companyResData, COMPANY_DASHBOARD_METRICS));
        }
      } catch (err) {
        console.error("Dashboard Fetch Error:", err);
        setError(err);
      } finally {
        setLoadingBtn(0);
      }
    };

    fetchData();
  }, [dateRange, useMockData, selectedArea, selectedUser, selectedDisease, activeFilter, refreshVersion]);

  const recordManualHealthData = useCallback(async (payload) => {
    const result = await postManualHealthData(payload);
    setRefreshVersion((version) => version + 1);
    return result;
  }, []);

  // Memoized table data for performance
  const userTableData = useMemo(() => ({
    columns: [
      { label: "ID", field: "UsuarioID", width: 50 },
      { label: "Nombre", field: "Nombre", sort: "asc", width: 200 },
      { label: "Nivel de estrés", field: "StressAvgAVG", width: 150 },
      { label: "Calidad de sueño", field: "SleepHoursAVG", width: 200 },
      { label: "Salud cardiovascular (HRV)", field: "HRVValueAvgAVG", width: 190 },
      { label: "Glucosa (prom.)", field: "GlucoseAVG", width: 150 },
      { label: "Presión arterial (prom.)", field: "BloodPressureAVG", width: 190 },
      { label: "Pasos", field: "StepsSUM", width: 150 },
      { label: "Km recorridos", field: "DistanceSUM", width: 150 },
      { label: "Calorias", field: "CaloriesSUM", width: 150 },
    ],
    rows: (userData || []).map((d) => ({
      ...d,
      Nombre: makeClickable(d.Nombre),
      StressAvgAVG: getTableValBadge(d.StressAvgAVG, stressRanges),
      SleepHoursAVG: getTableValBadge(d.SleepHoursAVG, sleepScoreRanges),
      HRVValueAvgAVG: getTableValBadge(d.HRVValueAvgAVG, cardiovascularHealthRanges),
      GlucoseAVG: d.GlucoseAVG === null || d.GlucoseAVG === undefined ? "—" : round(d.GlucoseAVG, 2),
      BloodPressureAVG: d.BloodPressureAVG === null || d.BloodPressureAVG === undefined
        ? "—"
        : round(d.BloodPressureAVG, 2),
      StepsSUM: round(d.StepsSUM, 2),
      DistanceSUM: round(d.DistanceSUM, 2),
      CaloriesSUM: round(d.CaloriesSUM, 2),
      clickEvent: () => setSelectedUser(d.UsuarioID),
    })),
  }), [userData, setSelectedUser]);

  return {
    state: { 
      areaData, 
      userData, 
      chartData, 
      prevChartData,
      heartRateData, 
      sleepData, 
      cardsData, 
      loadingBtn, 
      error, 
      searchByUser, 
      useMockData, 
      dateRange, 
      activeFilter, 
      selectedArea, 
      selectedUser,
      diseases,
      selectedDisease,
      poblationalData
    },
    actions: { 
      setSelectedArea, 
      setSelectedUser, 
      setSelectedDisease: handleSelectedDisease,
      setUseMockData, 
      setWeekly, 
      setMonthly, 
      setCustom, 
      setDateRange,
      recordManualHealthData,
    },
    derived: { userTableData }
  };
};
